import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight, ShieldCheck, RotateCcw, Download } from "lucide-react";
import { POLICY_OPTIONS, POLICY_TEST_SAMPLES } from "../../governance/govData";
import { useGov } from "../../governance/GovContext";
import { savePdf } from "../../governance/govPdf";
import { actionClasses } from "../../demo/demoData";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const DEFAULT = { trigger: "Personal data", userType: "Customer", channel: "Chatbot", severity: "High", action: "Redact", evidence: ["Create incident", "Notify compliance"] };

export default function PolicyBuilder() {
  const { policy, setPolicy } = useGov();
  const [cfg, setCfg] = useState(policy || DEFAULT);
  const [tested, setTested] = useState(false);
  const set = (k, v) => { const n = { ...cfg, [k]: v }; setCfg(n); setPolicy(n); setTested(false); };
  const toggleEvidence = (e) => { const has = cfg.evidence.includes(e); const ev = has ? cfg.evidence.filter((x) => x !== e) : [...cfg.evidence, e]; set("evidence", ev); };

  const sentence = `IF ${cfg.trigger.toLowerCase()} is detected in a ${cfg.userType.toLowerCase()} ${cfg.channel.toLowerCase()} interaction, THEN ${cfg.action.toLowerCase()}${cfg.evidence.length ? " AND " + cfg.evidence.map((e) => e.toLowerCase()).join(" AND ") : ""}.`;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6 space-y-4">
        {[["trigger", "Trigger"], ["userType", "User type"], ["channel", "Channel"], ["severity", "Severity"], ["action", "Action"]].map(([k, label]) => (
          <div key={k}>
            <label className="text-sm text-foreground">{label}</label>
            <Select value={cfg[k]} onValueChange={(v) => set(k, v)}>
              <SelectTrigger className="mt-1.5 bg-navy-800 border-white/12"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-navy-800 border-white/12">{POLICY_OPTIONS[k].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
        <div>
          <label className="text-sm text-foreground">Evidence</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {POLICY_OPTIONS.evidence.map((e) => (
              <button key={e} onClick={() => toggleEvidence(e)} className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${cfg.evidence.includes(e) ? "border-primary/50 bg-primary/12 text-foreground" : "border-white/10 text-muted-foreground hover:text-foreground hover:bg-white/5"}`}>{e}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Policy preview</p>
          <div className="mt-3 space-y-2 font-mono text-sm">
            <p><span className="text-primary">IF</span> <span className="text-foreground">{cfg.trigger}</span></p>
            <p><span className="text-primary">AND</span> user = <span className="text-foreground">{cfg.userType}</span> · channel = <span className="text-foreground">{cfg.channel}</span></p>
            <p><span className="text-primary">SEVERITY</span> <span className="text-foreground">{cfg.severity}</span></p>
            <p><span className="text-signal-amber">THEN</span> <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${actionClasses[cfg.action] || "text-foreground border-white/20"}`}>{cfg.action}</span></p>
            {cfg.evidence.map((e) => <p key={e}><span className="text-signal-green">EVIDENCE</span> {e}</p>)}
          </div>
          <p className="mt-4 text-sm text-muted-foreground">{sentence}</p>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => setTested(true)} className="bg-primary text-primary-foreground hover:bg-primary/90"><Play className="mr-1.5 h-4 w-4" /> Test this policy</Button>
            <Button variant="outline" onClick={() => { setCfg(DEFAULT); setPolicy(DEFAULT); setTested(false); }} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><RotateCcw className="mr-1.5 h-4 w-4" /> Reset</Button>
            <Button variant="outline" onClick={() => savePdf.policy(cfg)} data-testid="policy-download-pdf" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Download className="mr-1.5 h-4 w-4" /> Download PDF</Button>
          </div>
        </div>

        {tested && (
          <div className="rounded-lg border border-primary/25 bg-primary/[0.06] p-6">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Synthetic test interaction</p>
            <p className="mt-2 text-sm text-foreground font-mono">“{POLICY_TEST_SAMPLES[cfg.trigger]}”</p>
            <div className="mt-4 grid sm:grid-cols-3 gap-2 text-sm">
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3"><p className="text-xs text-muted-foreground">Detected</p><p className="text-foreground">{cfg.trigger}</p></div>
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3"><p className="text-xs text-muted-foreground">Action</p><p className="text-foreground">{cfg.action}</p></div>
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3"><p className="text-xs text-muted-foreground">Evidence</p><p className="text-foreground">{cfg.evidence.length} record(s)</p></div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">norse3 applies your rule exactly as configured — it does not invent its own regulatory decision.</p>
          </div>
        )}

        <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/book-a-demo">Book a norse3 walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
      </div>
    </div>
  );
}
